/**
 * The cookie consent the visitor gave on imt.fi.
 *
 * Read-only here. The banner lives in the legacy webshop
 * (`apps/webshop/src/app/common/analytics/consent/consent-overlay.component.ts`), which keeps the
 * decision in `localStorage` under `imtGivenConsent` and mirrors it into this `domain=.imt.fi`
 * cookie for us — `localStorage` is per-origin and this app is on `varaus.imt.fi`, a sibling
 * subdomain, so the cookie is the only way the answer reaches here. Same arrangement, and the same
 * reason, as {@link readHomeCityCookie} in `home-city-cookie.ts`; see `consent-cookie.ts` on the
 * webshop side for the writer. The campaign page at `www.imt.fi/jaakiekko` has a banner of its own
 * and publishes the same cookie (`apps/jaakiekko/src/lib/analytics/consent.ts`).
 *
 * Never written from this app. Two writers of one consent record is how a visitor ends up with a
 * decision they did not make.
 *
 * No cookie means no answer, which the caller must treat as "nothing consented to" rather than as
 * a default — a visitor who reaches `varaus.imt.fi` without passing through imt.fi (a campaign
 * deep link, a browser that dropped the cookie) has not agreed to anything yet.
 */

import { readCookie } from './cookie';

export interface ConsentChoice {
  /** "Toiminnalliset evästeet" — remembering the visitor's own choices. */
  functional: boolean;
  /** "Tilastointievästeet" — analytics measurement. */
  statistical: boolean;
  /** "Markkinointievästeet" — ad platforms and remarketing. */
  marketing: boolean;
}

const COOKIE_NAME = 'imt_consent';

/**
 * The only format this reads. An unrecognised version is treated as no answer: the shape may have
 * gained a category, and a missing category read as `false` under-reports while one read as `true`
 * would hand out consent nobody gave. Re-asking is the safe failure.
 */
const SUPPORTED_VERSION = '1';

/** The stored answer, or null when there is none (or it is unreadable). */
export function readConsentCookie(): ConsentChoice | null {
  const decoded = readCookie(COOKIE_NAME);
  if (decoded === null) return null;

  const fields = decoded.split('|');
  if (fields.length !== 4 || fields[0] !== SUPPORTED_VERSION) return null;

  // Strictly `'1'` or `'0'`: anything else is a value this reader does not understand, and
  // guessing at it is exactly what the version check above exists to prevent.
  const flags = fields
    .slice(1)
    .map((field) => (field === '1' ? true : field === '0' ? false : null));
  if (flags.some((flag) => flag === null)) return null;

  const [functional, statistical, marketing] = flags as boolean[];
  return { functional, statistical, marketing };
}

/**
 * Whether the choice permits analytics storage.
 *
 * Both categories, not just the statistical one — this mirrors the webshop's own
 * `StoredConsentService.analyticsCookieConsent`, which is what decides `analytics_storage` for
 * every GA4 hit from imt.fi. Deriving it differently here would measure the same visitor as
 * consented on one subdomain and not the other, which shows up as a split session rather than as
 * an obvious bug.
 */
export function allowsAnalytics(choice: ConsentChoice): boolean {
  return choice.functional && choice.statistical;
}
